import React from 'react';
import { documentToReactComponents, Options } from '@contentful/rich-text-react-renderer';
import { BLOCKS, MARKS, Document as ContentfulDocument, Node, INLINES } from '@contentful/rich-text-types';
import parse from 'html-react-parser';
import { ContentfulRendererProps, TextNodeTypes } from './ContentfulRenderer.types';
import InlineSVG from '../InlineSVG';
import VideoTypeRenderer from '../VideoTypeRenderer';
import NextImage from '../NextImage';
import RenderBodyCopy from '../RenderBodyCopy';
import Button from '../Button';
import TablePrimary from '../TablePrimary';
import ContentfulTextEditor from '../ContentfulTextEditor';
import ButtonScrollTo from '../ButtonScrollTo';
import NextLink from '../NextLink';
import ImageTextSecondary from '@src/components/Sections/ImageTextSecondary';
import CarouselCards from '@src/components/Sections/CarouselCards';
import ServiceOverview from '@src/components/Sections/ServiceOverview';
import isEmpty from '@src/utils/isEmpty';
import isFullUrl from '@src/utils/isFullUrl';
import determineMediaType from '@src/utils/determineMediaType';
const ContentfulRenderer: React.FC<ContentfulRendererProps> = ({
  document,
  links,
  inspectorProps
}) => {
  const options: Options = {
    renderMark: {
      [MARKS.BOLD]: (_node: React.ReactNode): React.ReactNode => <strong className='font-bold text-primary'>{_node}</strong>,
      [MARKS.ITALIC]: (_node: React.ReactNode): React.ReactNode => <em className='italic'>{_node}</em>,
      [MARKS.UNDERLINE]: (_node: React.ReactNode): React.ReactNode => <u>{_node}</u>,
      [MARKS.CODE]: (text: React.ReactNode): React.ReactNode => {
        // Check if text is a string, as parse function expects a string
        if (typeof text === 'string') {
          return parse(text);
        }
        // Handle other types or return text as is
        return text;
      },
      [MARKS.SUPERSCRIPT]: (_node: React.ReactNode): React.ReactNode => <sup>{_node}</sup>,
      [MARKS.SUBSCRIPT]: (_node: React.ReactNode): React.ReactNode => <sub>{_node}</sub>
    },
    renderNode: {
      [BLOCKS.HEADING_2]: (_node, children: React.ReactNode) => {
        const textNode = _node?.content?.[0] as TextNodeTypes;
        const id = textNode?.value?.trim().replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '-').toLowerCase();
        return <h2 data-id={id} className='mt-[1.6rem] inline-block text-[1.8rem] font-[500] leading-[2.5rem] text-body-copy--dark md:text-[2.4rem] md:leading-[3.2rem] 3xl:text-[3.2rem] 3xl:leading-[3.8rem]'>
            {children}
          </h2>;
      },
      [BLOCKS.HEADING_3]: (_node: Node, children: React.ReactNode) => <h3 className='inline-block text-[1.6rem] font-[500] leading-[2rem] text-body-copy--dark md:text-[1.85rem] md:leading-[3rem]'>
          {children}
        </h3>,
      [BLOCKS.PARAGRAPH]: (_node: Node, children: React.ReactNode) => {
        return <p className='inline-block w-[100%] text-[1.6rem] font-[300] leading-[2.56rem] text-body-copy--dark md:text-[1.8rem] md:leading-[2.88rem]'>
            {children}
          </p>;
      },
      [BLOCKS.EMBEDDED_ASSET]: _node => {
        const assetId = _node?.data?.target?.sys?.id;
        const asset = links?.assets?.block.find(asset => asset?.sys?.id === assetId);
        if (asset?.contentType === 'image/webp' || asset?.contentType === 'image/jpeg' || asset?.contentType === 'image/png' || asset?.contentType === 'image/gif') {
          return <NextImage className='my-[1rem]' src={asset?.url} aspectRatio={asset?.width / asset?.height} width={asset?.width} alt={asset?.description} caption={asset?.description} />;
        }
        if (asset?.contentType === 'image/svg+xml') {
          return <InlineSVG src={asset?.url} caption={asset?.description} />;
        }
        if (asset?.contentType?.includes('video/mp4') || asset?.contentType?.includes('video/x-m4v') || asset?.contentType?.includes('video/quicktime')) {
          return <VideoTypeRenderer data={{
            ...asset,
            type: 'HTML Video'
          }} />;
        }
        return null;
      },
      [BLOCKS.EMBEDDED_ENTRY]: _node => {
        const entryId = _node?.data?.target?.sys?.id;
        const entry = links?.entries?.block.find((entry: any) => entry?.sys?.id === entryId);
        if (entry?.__typename === 'Image') {
          // Object map for alignment styles
          const alignmentStyles: any = {
            Left: {
              marginLeft: '0',
              marginRight: 'auto'
            },
            Right: {
              marginLeft: 'auto',
              marginRight: '0'
            },
            Center: {
              margin: '0 auto'
            }
          };

          // Get the alignment style from the map, default to an empty object if not found
          const alignmentStyle = alignmentStyles[entry?.alignment] || {};
          const caption = entry?.bodyCopy?.json?.content;
          const imageElement = <div className='mb-[1.2rem]'>
              {determineMediaType({
              url: entry?.media?.url
            }) === 'image' ? <NextImage style={{
              width: `${entry?.width}%`,
              ...alignmentStyle
            }} objectFit='contain' imgClassName={`${entry?.boxShadow === 'Yes' ? 'box-shadow' : ''}`} src={entry?.media?.url} alt={entry?.media?.description} aspectRatio={entry?.media?.width / entry?.media?.height} width={entry?.media?.width} /> : null}

              {determineMediaType({
              url: entry?.media?.url
            }) === 'svg' ? <InlineSVG style={{
              width: `${entry?.width}%`,
              ...alignmentStyle
            }} src={entry?.media?.url} className={`${entry?.boxShadow === 'Yes' ? 'box-shadow' : ''}`} /> : null}

              {caption && <RenderBodyCopy className='bottom-[0] z-[100] mt-[2rem] text-center italic leading-[1.8rem] 2xl:mx-[3rem] [&*a]:text-primary [&>p>a]:!text-[1.4rem] [&>p>a]:font-[300] lg:[&>p>a]:!text-[1.6rem] [&>p]:!text-[1.4rem] [&>p]:font-[300] [&>p]:text-body-copy--dark lg:[&>p]:!text-[1.6rem]' bodyCopy={caption} />}
            </div>;
          if (!isEmpty({
            value: entry?.url
          })) {
            if (!isFullUrl({
              url: entry?.url
            })) {
              return <NextLink href={`${entry?.url}`}>{imageElement}</NextLink>;
            } else {
              return <a href={entry?.url} target='_blank' rel='noopener noreferrer'>
                  {imageElement}
                </a>;
            }
          }
          return imageElement;
        }
        if (entry?.__typename === 'TextBox') {
          const colors: any = {
            Orange: {
              primary: '#FF7A00',
              secondary: '#FFD9B7'
            },
            Grey: {
              primary: '#E5E7EB',
              secondary: '#FAFAFA'
            }
          };
          return <div style={{
            borderColor: colors?.[entry?.color]?.primary,
            backgroundColor: colors?.[entry?.color]?.secondary
          }} className={`rounded-[1rem] border-[0.3rem] border-l-[2rem] p-[1rem] text-left`}>
              <RenderBodyCopy bodyCopy={entry?.bodyCopy?.json?.content} entries={entry?.bodyCopy?.links?.entries?.block} className='inline-block w-[100%] text-[1.8rem] font-[300] leading-[2.56rem] text-body-copy--dark md:text-[1.8rem] md:leading-[2.88rem] [&>a]:text-[1.8rem] [&>a]:font-[400] [&>a]:underline [&>a]:hover:text-primary--v2' />
            </div>;
        }
        if (entry?.__typename === 'Video') {
          return <VideoTypeRenderer data={entry} />;
        }
        if (entry?.__typename === 'DuplexComponent') {
          return <ImageTextSecondary reversed={entry?.reversed} className='!mb-[0] !gap-x-[3.2rem] !px-[0] [&>div>header]:py-[0]' title={entry?.title} imgURL={entry?.mediaPrimary?.url} objectFit='contain' childrenElements={<>
                  <RenderBodyCopy bodyCopy={entry?.bodyCopy?.json?.content} className='inline-block w-[100%] text-[1.8rem] font-[300] leading-[2.56rem] text-body-copy--dark md:text-[1.8rem] md:leading-[2.88rem] [&>a]:text-[1.8rem] [&>a]:font-[400] [&>a]:underline [&>a]:hover:text-primary--v2' />
                </>} />;
        }
        if (entry?.__typename === 'Button') {
          return <div className=''>
              <Button primary href={entry?.url} btnText={entry?.text} target={entry?.target} />
            </div>;
        }
        if (entry.__typename === 'TextEditor') {
          return <ContentfulTextEditor>{parse(entry?.tinyMce)}</ContentfulTextEditor>;
        }
        if (entry?.__typename === 'Grid') {
          return <div className='post-col group'>
              {entry?.carousel === true ? <div className='post-car group'>
                  <CarouselCards id={entry?.scrollTo?.slice(1)} attr='clinicalsetting-component' title={entry?.title} topics={entry?.topicCollection?.items} sectionHeaderContainer='lg:!mb-[4.4rem] ' />
                </div> : <ServiceOverview id={entry?.scrollTo?.slice(1)} description={entry?.bodyCopy} services={entry?.topicCollection?.items} />}
            </div>;
        }
        return null;
      },
      [BLOCKS.UL_LIST]: (_node, children) => {
        return <ul className='list-disc pl-[2.4rem] text-[1.6rem] font-[300] leading-[2.5rem] text-body-copy--dark marker:text-body-copy--dark md:text-[1.8rem] md:leading-[2.88rem]	'>
            {children}
          </ul>;
      },
      [BLOCKS.OL_LIST]: (_node, children) => {
        return <ol className='pl-[2.4rem] text-[1.6rem] font-[300] leading-[2.5rem] text-body-copy--dark marker:text-body-copy--dark md:text-[1.8rem] md:leading-[2.88rem]	'>
            {children}
          </ol>;
      },
      [BLOCKS.LIST_ITEM]: (_node, children) => {
        // Check if children is an array and its first element is a React element
        if (Array.isArray(children) && React.isValidElement(children[0])) {
          // Assuming children[0] is a ReactElement, we can assert its type
          const firstChildElement = children[0] as React.ReactElement;

          // Check if the first child is a paragraph element
          if (firstChildElement?.type === 'p') {
            // Directly return the children of the paragraph to avoid nesting
            return <li>{firstChildElement?.props?.children}</li>;
          }
        }

        // Otherwise, wrap the children in a paragraph tag
        return <li>{children}</li>;
      },
      [INLINES.HYPERLINK]: (_node, children) => {
        const href = _node?.data?.uri;
        const target = href?.startsWith('/') || href?.includes('valdperformance.com') ? '_self' : '_blank';
        if (href?.startsWith('#')) {
          return <ButtonScrollTo children={children} href={href} />;
        }
        return <a href={href} target={target} rel='noopener noreferrer' className='font-[400] underline hover:text-primary--v2'>
            {children}
          </a>;
      },
      [BLOCKS.QUOTE]: _node => {
        const content = documentToReactComponents(_node as ContentfulDocument, {
          renderNode: {
            [BLOCKS.PARAGRAPH]: (_node, children) => children,
            [BLOCKS.QUOTE]: (_node, children) => children
          },
          renderMark: {
            [MARKS.CODE]: (_node: React.ReactNode) => <span>{parse(`${_node} `)}</span>,
            [MARKS.BOLD]: (_node: React.ReactNode): React.ReactNode => <strong className='font-[500] text-primary'>{_node}</strong>,
            [MARKS.SUPERSCRIPT]: (_node: React.ReactNode): React.ReactNode => <sup>{_node}</sup>,
            [MARKS.SUBSCRIPT]: (_node: React.ReactNode): React.ReactNode => <sub>{_node}</sub>,
            [MARKS.ITALIC]: (_node: React.ReactNode): React.ReactNode => <em>{_node}</em>
          }
        });
        return <blockquote className='my-[1rem] text-pretty border-l-[0.5rem] border-primary pl-[1.6rem] text-[1.6rem] font-[400] leading-[2.4rem] text-[500] text-body-copy md:text-[2.4rem] md:leading-[3.6rem] [&>a]:font-[400] [&>a]:!underline'>
            {content}
          </blockquote>;
      },
      [BLOCKS.HR]: () => <hr className='border-border'></hr>,
      [BLOCKS.TABLE]: _node => {
        const content = documentToReactComponents(_node as ContentfulDocument, {
          renderNode: {
            [BLOCKS.TABLE]: (_node, children: any) => {
              const headerRow = children?.[0];
              const bodyRows = children?.slice(1);
              const checkMergedRow = (element: any) => {
                if (element?.type === 'span' && element?.props?.children?.slice(0, 3) === 'row') {
                  return true;
                }
                // If the current element has children, recursively check each child
                if (element?.props && Array.isArray(element?.props?.children)) {
                  for (const child of element.props.children) {
                    if (typeof child === 'object' && checkMergedRow(child)) {
                      return true;
                    }
                  }
                }
                // If the current element is an array, recursively check each element
                if (Array.isArray(element)) {
                  for (const item of element) {
                    if (checkMergedRow(item)) {
                      return true;
                    }
                  }
                }
                // No matching element found
                return false;
              };
              const mergedRow = checkMergedRow(children);
              return <TablePrimary headerRow={headerRow} bodyRows={bodyRows} className={` ${mergedRow ? '[&>tbody>tr]:bg-background-secondary' : 'odd:[&>tbody>tr]:bg-background-grey-light even:[&>tbody>tr]:bg-background-secondary'}`} />;
            },
            [BLOCKS.TABLE_HEADER_CELL]: (_node, children) => <td className='bg-primary py-[1rem] pl-[2rem] align-top text-2xl text-[1.8rem] font-[500] leading-normal text-body-copy--light [&>a]:font-[300] [&>a]:underline'>
                {children}
              </td>,
            [BLOCKS.TABLE_ROW]: (_node, children) => {
              return <tr className='font-[400] text-body-copy'>{children}</tr>;
            },
            [BLOCKS.TABLE_CELL]: (_node, children) => {
              const merge = (children as any)?.[0]?.[1]?.props?.children;
              const mergeType = merge !== 'hidden' && merge?.slice(0, 3);
              const mergeQuantity = merge !== 'hidden' && merge?.slice(4, 5);
              const renderWithLineBreak = (content: any) => {
                return content?.split('\n')?.map((item: any, index: any) => <React.Fragment key={index}>
                    {item}
                    <br />
                  </React.Fragment>);
              };
              return <td colSpan={mergeType === 'col' ? mergeQuantity : 1} rowSpan={mergeType === 'row' ? mergeQuantity : 1} className={`content-center p-[1rem] align-top text-[1.8rem] font-[400] leading-normal text-body-copy ${merge === 'hidden' ? 'hidden' : ''}`}>
                  {React?.Children?.map(children, child => typeof child === 'string' ? renderWithLineBreak(child) : child)}
                </td>;
            },
            [BLOCKS.PARAGRAPH]: (_node, children) => {
              return children;
            },
            [INLINES.EMBEDDED_ENTRY]: _node => {
              const entryId = _node?.data?.target?.sys?.id;
              const entry = links?.entries?.inline.find(entry => entry?.sys?.id === entryId);
              if (entry?.__typename === 'Image') {
                return <NextImage className='mx-auto my-[1rem] overflow-hidden' style={{
                  width: `${entry?.width}%`
                }} src={entry?.media?.url} aspectRatio={entry?.media?.width / entry?.media?.height} width={entry?.media?.width} alt={entry?.media?.description} caption={entry?.media?.description} />;
              }
              return null;
            }
          }
        });
        return <>{content}</>;
      }
      // Add other block types with their corresponding types...
    }
  };
  return <div className='flex flex-col gap-[2.4rem]' {...inspectorProps} data-sentry-component="ContentfulRenderer" data-sentry-source-file="ContentfulRenderer.tsx">
      {documentToReactComponents(document as ContentfulDocument, options)}
    </div>;
};
export default ContentfulRenderer;